import * as a from '@mitranim/js/all.mjs'
import * as dr from '@mitranim/js/dom_reg.mjs'
import * as c from './conf.mjs'
import * as u from './util.mjs'
import {E, A} from './util.mjs'
import * as n from './net.mjs'
import * as eim from './elem-input-md.mjs'
import * as ef from './elem-form.mjs'
import * as eb from './elem-btn.mjs'
import * as effc from './elem-form-field-checkbox.mjs'
import * as s from './views/svg.mjs'

const AI_CONVO_DESC = false
const AI_CONVO_MOCK_PROMPT = ``
// const AI_CONVO_MOCK_PROMPT = `Tell me about effective ways to lose excess weight. #nutriology`

export class AiConvo extends u.Elem {
  static {dr.reg(this)}
  inited = false

  init(props) {
    this.hidden = true
    return this.setProps(props)
  }

  async connectedCallback() {
    if (this.inited) return

    // Wait for the other element classes in this file to register.
    await undefined

    const props = this.getProps()
    const id = this.getId()
    const hist = new AiConvoHist(this).init()
    const form = new AiConvoForm(this).init({
      ...a.optDict(props?.form),
      hasMsgs: !!id,
    })

    try {
      if (id) {
        const ctx = this.ctx.sub()
        this.drawLoader(ctx)
        const convo = await n.aiConvo(ctx, id)
        for (const val of a.reverse(convo.msgs)) hist.addMsg(val)
      }
    }
    catch (err) {
      if (!a.isErrAbort(err)) hist.addMsg(errMsg(err))
    }
    finally {
      this.drawConvo(hist, form)
      form.finalize(hist)
      this.inited = true
    }

    const prompt = props?.defaultPrompt
    if (prompt && !hist.hasMsgs()) {
      this.setPrompt(prompt)
      form.exec()
    }
  }

  drawLoader(ctx) {
    return E(
      this,
      {class: `self-cen col-cen-cen gap`, hidden: false},
      E(`span`, {}, `Loading AI conversation... (`,
        E(
          `button`,
          {type: `button`, class: `decorate-link`, onclick: () => {ctx.deinit()}},
          `skip`
        ),
        `)`,
      ),
      Spinner(),
    )
  }

  drawConvo(...chi) {
    const {class: cls, ...props} = a.laxDict(this.getProps()?.attrs)

    return E(
      this,
      {
        /*
        In ascending mode, we give `AiConvoHist` a `padding-bottom` instead
        of using `gap` here. This allows us to properly scroll it to bottom
        when overlaid by the sticky form.
        */
        class: u.cls(`col-sta-str w-full`, cls, AI_CONVO_DESC && `gap`),
        hidden: false,
        ...props,
      },
      reverseOpt(AI_CONVO_DESC, chi),
    )
  }

  setPrompt(src) {this.desc(AiConvoForm).setPrompt(src)}

  getId() {
    return a.vac(this.dataset.id || this.bank().get(this.getKey()))
  }

  setId(val) {
    a.optStr(val)
    this.data().id = val
    this.bank().set(this.getKey(), val).write()
  }

  getKey() {return a.optStr(this.getProps()?.diskKey) || c.DISK_KEY_AI_MSG_ID}

  clearHistory() {
    this.setId()
    const hist = this.desc(AiConvoHist)
    hist.clearAll()
    this.desc(AiConvoForm).finalize(hist)
    this.scrollIntoViewIfNeeded()
  }

  // Invoked by `ElemReuse`.
  onReuse(prompt) {if (prompt) this.setPrompt(prompt)}
}

// TODO: consider supporting image pasting.
const AiConvoInp = eim.MdTextarea

class AiConvoForm extends ef.Form {
  static {dr.reg(this)}

  init({text, hasMsgs, sticky, search} = {}) {
    return E(
      this,
      {
        class: u.cls(
          `col-sta-sta w-full gap`,
          !AI_CONVO_DESC && sticky && `sticky bot-0 bg-white fg-black`,
        ),
      },
      E(`div`, {class: `col-sta-sta brd-1 round-x2 shad-light w-full pad gap`},
        E(new AiConvoInp(this), {
          class: `w-full h-5`,
          name: `body`,
          placeholder: `Ask anything!`,
        }, a.optStr(text) || a.vac(!c.PROD) && AI_CONVO_MOCK_PROMPT),
        E(`hr`, {class: `hr-h fg-pale`}),
        E(`div`, {class: `row-bet-cen w-full gap`},
          new SearchToggle(this).initDefault(search || !hasMsgs),
          Action(),
          E(`div`, {class: `row-sta-cen gap`},
            new BtnClearHistory(this).init(hasMsgs),
            E(`hr`, {class: `hr-v fg-pale`}),
            BtnSubmit(),
          ),
        ),
      ),
    )
  }

  /*
  TODO support cancelation:
  - Use element-local context which is deinited on `.disconnectedCallback`.
  - Provide a way to cancel the current request.
  */
  async perform() {
    let {body: userPrompt, ...params} = this.formData()
    userPrompt = a.trim(userPrompt)
    if (!userPrompt) return

    const inp = this.desc(AiConvoInp)
    const convo = this.anc(AiConvo)
    const hist = convo.desc(AiConvoHist)
    const prevId = convo.getId()
    const msgs = prevId ? [] : hist.msgs()

    msgs.push({role: c.AI_MSG_ROLE_USER, body: userPrompt})
    hist.clearErrs()

    if (c.DEV_AI_MOCK) {
      const mod = await u.imp(import(`./util-mock-text.mjs`))
      const msg = mod.nextMockAiMsg()
      hist.addMsg(msg)
      this.scrollConvo()
      return
    }

    let userMsg
    let botMsg
    let nextId
    let gotText
    let done

    try {
      const res = await n.aiAskStream(this.ctx, {msgs, prevId, ...params})

      for await (const src of u.streamLines(res.body)) {
        const {type, body} = decodeAiStreamEvent(src)

        if (type === `done`) {
          done = true
          break
        }

        if (type === `error`) {
          u.logErr(`error event in AI stream:`, src)
          throw Error(body || `unexpected error`)
        }

        if (type === `id`) {
          nextId = a.optStr(body)
          continue
        }

        if (type === `action`) {
          this.setAction(`Current action: `, body, `...`)
          continue
        }

        if (type !== `append` || !body) continue

        gotText = true
        this.setAction()

        if (!userMsg) {
          userMsg = hist.addMsg({role: c.AI_MSG_ROLE_USER, body: userPrompt})
          inp.value = ``
        }

        botMsg ||= hist.addMsg({role: c.AI_MSG_ROLE_BOT})
        botMsg.appendText(body)
      }

      if (!done) throw Error(`AI response stream prematurely terminated`)

      if (!gotText) {
        aiConvoMsgMarkErr(hist.addMsg(errMsg(`unexpected empty response from AI`)))
        return
      }

      if (nextId) convo.setId(nextId)
      this.scrollConvo()
    }
    catch (err) {
      u.logErr(err)
      inp.replaceAll(userPrompt)
      aiConvoMsgMarkErr(userMsg)
      aiConvoMsgMarkErr(botMsg)
      aiConvoMsgMarkErr(hist.addMsg(errMsg(err)))
    }
    finally {this.finalize(hist)}
  }

  setPrompt(src) {this.desc(AiConvoInp).replaceAll(a.laxStr(src))}

  setAction(...chi) {
    const tar = u.nodeByAttr(this, `data-role`, `action`)
    E(tar, {hidden: !a.vac(chi)}, chi)
  }

  finalize(hist) {
    this.setAction()
    this.desc(BtnClearHistory).toggle(hist.hasMsgs())
  }

  scrollConvo() {
    if (AI_CONVO_DESC) return
    if (this.style.position === `sticky`) return
    u.scrollIntoViewBottom(this)
  }
}

function Action() {
  return E(`div`, {hidden: true, dataset: {role: `action`}})
}

class SearchToggle extends effc.FormFieldBool {
  static {dr.reg(this)}
  get Label() {return effc.CheckboxLabelToggle}

  initDefault(val) {
    val = a.laxBool(val)
    this.initFromSpec({
      name: `enableSiteSearch`,
      value: val,
      title: `Site search`,
    })
    return this
  }
}

class BtnClearHistory extends eb.Btn {
  static {dr.reg(this)}

  init(hasMsgs) {
    return E(
      this,
      {
        class: `btn-icon --std`,
        type: `button`,
        'aria-label': `Clear history`,
      },
      s.Trash(),
    ).toggle(hasMsgs)
  }

  toggle(hasMsgs) {
    this.hidden = !hasMsgs
    return this
  }

  perform() {this.anc(AiConvo).clearHistory()}
}

// Copied from `promos.mjs`, TODO dedup.
function BtnSubmit() {
  return E(
    new eb.BtnSubmit(),
    A
      .tooltip(`Ctrl+Enter`)
      .submit()
      .ssrDisabled()
      .cls(`btn-service --icon`),
    s.ArrowUp()
  )
}

class AiConvoHist extends u.Elem {
  static {dr.reg(this)}

  init() {
    /*
    The bottom padding is used instead of a having a `gap` on the parent.
    This allows us to properly scroll to bottom when overlaid by sticky form.
    TODO: avoid padding when empty (no msgs).
    */
    return E(this, {
      class: u.cls(`col-sta-sta w-full gap`, !AI_CONVO_DESC && `pad-b-1`),
    })
  }

  addMsg(inp) {
    const msg = this.makeMsg(inp)
    return AI_CONVO_DESC ? u.prependChild(this, msg) : this.appendChild(msg)
  }

  makeMsg(inp) {return new AiConvoMsg(this).init(inp)}

  msgs() {
    return reverseOpt(
      AI_CONVO_DESC,
      a.mapCompact(this.childNodes, aiConvoMsg),
    )
  }

  hasMsgs() {return !!this.childElementCount}

  clearErrs() {
    for (const val of this.children) {
      if (`err` in val.dataset) val.remove()
    }
  }

  clearAll() {this.innerHTML = ``}

  scrollToBottomIfNeeded() {
    if (AI_CONVO_DESC) return
    const top = this.anc(AiConvo)?.desc(AiConvoForm)?.getBoundingClientRect().top
    const bot = this.getBoundingClientRect().bottom
    const diff = bot - top
    if (diff) window.scrollBy(0, diff)
  }
}

const AI_MSG_FORMAT_MD = `md` // Implicit default.
const AI_MSG_FORMAT_TXT = `txt`

class AiConvoMsg extends u.Elem {
  static {dr.reg(this)}

  // We used to store this in `.dataset`, but it was getting in the way when
  // browsing elements in development.
  body = ``

  init({role, body, format, isErr}) {
    E(
      this,
      {
        class: u.cls(
          `responsive-typography-article`,
          (a.vac(role === c.AI_MSG_ROLE_BOT) && `w-full`),
          (
            a.vac(role === c.AI_MSG_ROLE_USER) &&
            `bg-zinc-100 round font-p maw-col-6 pad self-end`
          ),
          (isErr && `fg-error`)
        ),
        dataset: {role, format, err: u.markerStr(isErr)},
      },
    )
    this.appendText(body)
    return this
  }

  appendText(src) {
    src = this.appendSrcText(src)
    const {format} = this.dataset
    if (!format || format === AI_MSG_FORMAT_MD) {
      this.innerHTML = new u.MdOptAi().run(src)
    }
    else {
      this.textContent = src
    }
    this.anc(AiConvoHist)?.scrollToBottomIfNeeded()
  }

  appendSrcText(src) {return this.body = a.laxStr(this.body) + a.laxStr(src)}
}

function aiConvoMsg(src) {
  if (isAiConvoMsgErr(src)) return undefined
  if (!a.isElement(src)) return undefined
  const role = src?.dataset?.role
  const body = src?.body
  return a.vac(body) && {role, body}
}

function isAiStreamEvent(val) {
  return a.isStruct(val) && a.isValidStr(val.type)
}

function decodeAiStreamEvent(src) {
  const out = a.jsonDecode(src)
  if (!isAiStreamEvent(out)) {
    throw Error(`internal error: expected AI stream event, got ${a.show(src)}`)
  }
  return out
}

function aiConvoMsgMarkErr(tar) {
  if (!a.optElement(tar)) return
  tar.dataset.err = ``
  tar.style.opacity = `0.8`
}

function isAiConvoMsgErr(val) {return a.isElement(val) && `err` in val.dataset}

function reverseOpt(desc, src) {return desc ? a.reverse(src) : src}

function errMsg(err) {
  err = u.errStrSimple(err)
  return {
    role: c.AI_MSG_ROLE_SYSTEM,
    body: err ? `Error: ` + err : `Unknown error`,
    isErr: true,
    format: AI_MSG_FORMAT_TXT,
  }
}

// Copied from `misc.mjs` to avoid a large import.
function Spinner() {
  return E(`span`, {class: `spinner --inline`}, a.times(4, Span))
}
function Span() {return E(`span`)}

export class BtnAiSugg extends eb.Btn {
  static {dr.reg(this)}

  // Do not change the name: hardcoded in `cs-core`.
  // SYNC[btn_ai_sugg].
  customName = `btn-ai-sugg`
  init(...chi) {return E(this, {type: `button`, class: `btn-ai-sugg`}, ...chi)}

  perform() {
    const src = this.textContent
    if (!src) return

    const convo = (
      this.anc(AiConvo) ||
      a.descendant(this.findAnc(isRoot), AiConvo)
    )
    convo.setPrompt(src)
  }
}

// SYNC[ai_convo_root].
function isRoot(val) {return val?.dataset?.role === `ai-convo-root`}
